/**
 * ============================================================================
 * EMBED MODE — standalone vs embedded shell detection (spec embedded-mode).
 * ============================================================================
 *
 * standalone — the app in its own tab: full shell (header banner, side nav
 *              rail, footer).
 * embedded   — inside a host <iframe> (e.g. the project portal's Execution
 *              Metrics tab). The host has its own chrome, so the banner,
 *              rail, and footer are hidden; the page-tabs strip and
 *              dashboard switcher keep working in-frame.
 *
 * Detection, resolved once at boot (the mode never changes within a page
 * lifetime):
 *   1. `?embed=` override wins: `1|true|yes` → embedded, anything else →
 *      standalone. Testing hook + escape hatch. Affirmative-only so an
 *      unrecognized value never strips chrome.
 *   2. Otherwise framed (`window.self !== window.top`) → embedded, so any
 *      iframe host gets the reduced chrome with no host-side changes.
 *
 * Comparing window identities never throws cross-origin (only reading the
 * top frame's properties does); `window.top` is null-guarded for detached
 * contexts.
 */

/** Pure detection — exported for tests; callers use IS_EMBEDDED. */
export function detectEmbedMode(
  search: string,
  isFramed: boolean,
): 'standalone' | 'embedded' {
  const embed = new URLSearchParams(search).get('embed');
  if (embed !== null) {
    return /^(1|true|yes)$/i.test(embed) ? 'embedded' : 'standalone';
  }
  return isFramed ? 'embedded' : 'standalone';
}

/** Is this window rendered inside another page's frame? */
function framed(): boolean {
  return window.top !== null && window.self !== window.top;
}

/**
 * The app's shell mode, fixed at boot. Reads the INITIAL URL — client-side
 * navigation rewrites the query string, so the flag is captured before the
 * router mounts.
 */
export const IS_EMBEDDED: boolean =
  detectEmbedMode(window.location.search, framed()) === 'embedded';
